import type { BuildingType } from '$lib/types'
import { readVar } from '$lib/theme'

/**
 * Theme-aware colour bridge for the Pixi renderer. Every value here is a
 * function over a CSS custom property; layers call them at draw time so a
 * theme switch shows up on the next redraw without any cache to invalidate.
 *
 * Only solid-hex variables are exposed. rgba() literals (semitransparent
 * washes, shadows, focus rings) live in the CSS layer and never cross into
 * Pixi.
 *
 * The hex → number parsing helper lives in `$lib/theme/variables.ts` so the
 * theme module owns CSS-variable concerns end-to-end.
 */

const CAT_VAR: Record<BuildingType, string> = {
  residential: '--cat-residential',
  production: '--cat-production',
  goods: '--cat-goods',
  culture: '--cat-culture',
  decoration: '--cat-decoration',
  military: '--cat-military',
  street: '--cat-street',
  greatbuilding: '--cat-greatbuilding',
  main_building: '--cat-mainbuilding',
  // Variants without a dedicated category slot fall back to the closest analogue.
  diplomacy: '--cat-culture',
  random_production: '--cat-production',
  cultural_goods_production: '--cat-goods',
  static_provider: '--cat-culture',
  tower: '--cat-greatbuilding',
  placement: '--cat-decoration',
  off_grid: '--cat-unknown',
  outpost_ship: '--cat-unknown',
  friends_tavern: '--cat-unknown',
  hub_main: '--cat-unknown',
  hub_part: '--cat-unknown',
  impediment: '--cat-goods',
  clan_power_production: '--cat-greatbuilding',
  unknown: '--cat-unknown',
}

export function colourFor(type: BuildingType | 'unknown'): number {
  return readVar(CAT_VAR[type] ?? '--cat-unknown')
}

export const colours = {
  bgBase: () => readVar('--bg-base'),
  unlockedFill: () => readVar('--surface-base'),
  gridLine: () => readVar('--grid-line'),
  buildingBorder: () => readVar('--building-border'),
  unknownHatch: () => readVar('--cat-unknown'),
  validationError: () => readVar('--validation-error'),
  selectionOutline: () => readVar('--selection-outline'),
  hoverOutline: () => readVar('--gold-bright'),
  ghostFill: () => readVar('--ghost-fill'),
  ghostStroke: () => readVar('--ghost-stroke'),
  roadPaintPreview: () => readVar('--road-paint-preview'),
}
